// App-owned selection. Owns the styled `.selected` class and the Set of
// selected grid cells end-to-end — the single writer of `.selected`.
//
// SortableJS's MultiDrag plugin is demoted to a dumb drag engine: it keeps its
// own unstyled, app-invisible `sjs-selected` class/list, which app.js syncs to
// this Set right before a drag arms (see the pointerdown sync there). Nothing
// here touches Sortable; nothing in Sortable styles the grid.
//
// Mouse click toggles a cell; Shift+click adds the contiguous run from the last
// plain-clicked cell (the anchor) to the clicked cell. Touch: a plain tap never selects (it anchors
// the preview in preview.js); a stationary long-press (>= LONG_PRESS_MS) enters
// range-select mode. The gesture paints add-or-remove decided by the origin
// cell's state when it engages: pressing an unselected cell selects the origin
// and extends by adding a contiguous run; pressing an already-selected cell
// deselects the origin and paints the run off (deselect the same way you
// select). Sliding the finger extends the run from the origin to the cell under
// it (either direction), lifting commits, a cancel / a second finger rolls
// back. Keyboard Space/Shift+Space (app.js's shared keydown) reuse the exact
// click/Shift+click paths (select/selectRange) on preview.js's active cell.
// `grid` and `onChange` are owned by app.js and passed in explicitly;
// `onChange(size)` fires after every membership change (drives the cancel fob).
import { tapDist, cellAt } from './util.js';
import { cancelPendingDrag } from './gestures.js';

// 500ms in production; a test harness may lower it via the timing override
// (see the central definition + comment in js/app.js). Unset in production →
// exactly 500.
const LONG_PRESS_MS = globalThis.__MS_TEST_TIMINGS__?.longPressMs ?? 500;
const MOVE_TOL = 10;   // px of finger travel that aborts the long-press timer (matches util.js's ptrMoved)

export function initSelection({ grid, onChange }) {
  const set = new Set();   // cell elements
  const emit = () => onChange?.(set.size);

  function toggle(cell) {
    if (set.has(cell)) { set.delete(cell); cell.classList.remove('selected'); }
    else { set.add(cell); cell.classList.add('selected'); }
    emit();
  }
  // Plain select (mouse click / keyboard Space): toggle the cell and re-anchor
  // there, so a following range-select measures from it.
  function select(cell) { toggle(cell); anchor = cell; }
  // Range select (Shift+click / Shift+Space): add the contiguous run from the
  // anchor to `cell` (never toggling off), leaving the anchor put so the range
  // can be re-adjusted from the same origin. With no valid anchor it degrades to
  // a plain select.
  function selectRange(cell) {
    if (anchor && grid.contains(anchor)) {
      for (const c of cellsBetween(anchor, cell)) if (!set.has(c)) { set.add(c); c.classList.add('selected'); }
      emit();
    } else select(cell);
  }
  function has(cell) { return set.has(cell); }
  // DOM order, so a group drag moves cells in the order they appear.
  function cells() { return [...grid.children].filter(c => set.has(c)); }
  function clear() {
    for (const c of set) c.classList.remove('selected');
    set.clear();
    anchor = null;
    emit();
  }
  // Make the selection exactly `want`, writing `.selected` for the diff only.
  // `want` must be a distinct Set (callers pass a fresh one) — clearing `set`
  // first would otherwise empty `want` mid-reconcile.
  function applySet(want) {
    if (want === set) return;
    for (const c of set) if (!want.has(c)) c.classList.remove('selected');
    for (const c of want) if (!set.has(c)) c.classList.add('selected');
    set.clear();
    for (const c of want) set.add(c);
    emit();
  }

  // Anchor for Shift+click range selection: the last cell a plain click landed
  // on. Held fixed across Shift+clicks so the range can be re-adjusted from the
  // same origin; reset by clear() (render() clears too, so it never goes stale).
  let anchor = null;

  // Left-button mouse click, no meaningful travel, not mid-drag. tapDist reads
  // util.js's global pointerdown tracker — a drag's release lands far from its
  // press, so it's filtered out here even before body.dragging clears.
  grid.addEventListener('pointerup', (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    if (document.body.classList.contains('dragging')) return;
    if (tapDist(e) > 10) return;
    const cell = e.target.closest?.('.cell');
    if (!cell || !grid.contains(cell)) return;
    // Shift+click adds the contiguous run from the anchor to the clicked cell
    // (never toggling off) and leaves the anchor put; a plain click toggles the
    // one cell and re-anchors there. Same paths keyboard Space / Shift+Space use.
    if (e.shiftKey) selectRange(cell);
    else select(cell);
  });

  // ---------- Touch long-press range-select ----------
  // origin cell + its press coords, the long-press timer, the tracked finger,
  // the snapshot of the selection when the gesture engaged, whether we're live,
  // and whether the run adds ('add') or removes ('remove') — set at fire().
  let origin = null, originX = 0, originY = 0;
  let pressTimer = null, trackedId = null, base = null, active = false, mode = 'add';
  const down = new Set();   // active touch pointerIds — tells a first finger from a second

  // Contiguous DOM-order run between two cells (inclusive, either direction).
  function cellsBetween(a, b) {
    const kids = [...grid.children];
    let i = kids.indexOf(a), j = kids.indexOf(b);
    if (i < 0 || j < 0) return [];
    if (i > j) [i, j] = [j, i];
    return kids.slice(i, j + 1);
  }
  // Displayed selection = base with the origin..target run painted on ('add')
  // or off ('remove'). Re-derived wholesale each move — no incremental diffing.
  function update(target) {
    const want = new Set(base);
    const run = cellsBetween(origin, target);
    if (mode === 'remove') for (const c of run) want.delete(c);
    else                   for (const c of run) want.add(c);
    applySet(want);
  }
  function reset() {
    clearTimeout(pressTimer); pressTimer = null;
    origin = null; trackedId = null; base = null; active = false; mode = 'add';
  }
  function fire() {
    pressTimer = null;
    // Cancel Sortable's armed drag FIRST — its synthetic pointercancel
    // (pointerId 0) dispatches synchronously and must not trip the abort
    // listener below (which ignores any pointerId but the tracked finger's).
    cancelPendingDrag();
    active = true;
    // Add when the origin is unselected, remove when it's already selected —
    // decided now, before base is snapshotted, so the run paints consistently.
    mode = set.has(origin) ? 'remove' : 'add';
    base = new Set(set);   // snapshot before toggling the origin
    try { grid.setPointerCapture(trackedId); } catch {}
    document.body.classList.add('range-selecting');
    update(origin);        // toggle the origin cell — the instant visual cue
  }
  function commit() {
    active = false;
    reset();
    // Keep body.range-selecting through the rest of THIS pointerup dispatch
    // (preview.js's tap handler and viewer.js's window-capture double-tap
    // detector both read it) — remove it deferred so the commit stays inert.
    setTimeout(() => document.body.classList.remove('range-selecting'), 0);
  }
  // Roll back to the pre-gesture selection — the least-surprising outcome of an
  // aborted gesture (a second finger handing off to pinch, or a real cancel).
  function abort() {
    if (active) {
      applySet(base);
      setTimeout(() => document.body.classList.remove('range-selecting'), 0);
    }
    reset();
  }

  // Arm on the first touch landing on a cell. A second concurrent finger never
  // arms (down.size already > 1 by the time this bubble listener runs — the
  // document-capture listener below counts pointers first and aborts).
  grid.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'touch' || down.size > 1) return;
    const cell = e.target.closest?.('.cell');
    if (!cell || !grid.contains(cell)) return;
    reset();
    origin = cell; originX = e.clientX; originY = e.clientY; trackedId = e.pointerId;
    pressTimer = setTimeout(fire, LONG_PRESS_MS);
  });
  grid.addEventListener('pointermove', (e) => {
    if (e.pointerId !== trackedId) return;
    if (active) {
      const c = cellAt(e.clientX, e.clientY);   // a move over the gap/panel keeps the last range
      if (c) update(c);
    } else if (pressTimer && Math.hypot(e.clientX - originX, e.clientY - originY) > MOVE_TOL) {
      clearTimeout(pressTimer); pressTimer = null;   // moved before firing -> it's a drag/scroll, not a long-press
    }
  });
  grid.addEventListener('pointerup', (e) => {
    if (e.pointerId !== trackedId) return;
    if (active) commit(); else reset();
  });

  // Count active touch pointers (capture, document — sees every finger). A
  // second finger while one is down hands off to pinch: abort the gesture.
  document.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'touch') return;
    if (down.size >= 1) abort();
    down.add(e.pointerId);
  }, true);
  const releasePtr = (e) => down.delete(e.pointerId);
  document.addEventListener('pointerup', releasePtr, true);
  // Abort on a real cancel for the tracked finger. Ignores fire()'s own
  // synthetic cancel (pointerId 0) and any other finger's — the synthetic one
  // dispatches on document, where this capture listener also catches real
  // browser cancels; it never reaches grid-level listeners.
  document.addEventListener('pointercancel', (e) => {
    down.delete(e.pointerId);
    if (trackedId !== null && e.pointerId === trackedId) abort();
  }, true);

  // touch-action: pan-y (styles.css) can't be flipped mid-gesture, so this
  // permanent non-passive listener suppresses scrolling only while range-select
  // is live — the browser then never claims the gesture, so no pointercancel
  // arrives. No conflict with layout.js's pinch listener (it preventDefaults
  // only with two pointers down; by then we've already aborted).
  grid.addEventListener('touchmove', (e) => { if (active) e.preventDefault(); }, { passive: false });

  return { toggle, select, selectRange, has, cells, clear };
}
